/**
 * Platform permission registry (UI).
 * Sync version must match API: src/common/permissions/platform-permission.registry.ts
 * PLATFORM_PERMISSION_REGISTRY_VERSION = 1
 */
import {
  mutationAction,
  readAction,
  type PermissionActionDef,
  type PermissionModuleDef,
  type PermissionLevel,
  PERMISSION_LEVELS,
} from './permission.types';

export {
  PLATFORM_PERMISSION_REGISTRY_VERSION,
  PERMISSION_LEVELS,
  PERMISSION_LEVEL_NO_ACCESS,
  PERMISSION_LEVEL_READ_ONLY,
  PERMISSION_LEVEL_EXECUTE,
  READ_LEVELS,
  MUTATION_LEVELS,
  readAction,
  mutationAction,
} from './permission.types';
export type {
  PermissionLevel,
  PermissionActionDef,
  PermissionModuleDef,
  RoleModulePermission,
  EffectivePermissions,
} from './permission.types';

function moduleDef(
  partial: Omit<PermissionModuleDef, 'kind' | 'accessOnly' | 'actions'> & {
    accessOnly?: boolean;
    actions?: PermissionActionDef[];
  },
): PermissionModuleDef {
  return {
    kind: 'module',
    accessOnly: partial.accessOnly ?? false,
    actions: partial.actions ?? [],
    moduleKey: partial.moduleKey,
    label: partial.label,
    route: partial.route,
    parentKey: partial.parentKey,
  };
}

function groupDef(moduleKey: string, label: string): PermissionModuleDef {
  return {
    moduleKey,
    label,
    kind: 'group',
    accessOnly: true,
    actions: [],
  };
}

export const PLATFORM_PERMISSION_REGISTRY: PermissionModuleDef[] = [
  moduleDef({
    moduleKey: 'dashboard',
    label: 'Dashboard',
    route: '/platform/dashboard',
    accessOnly: true,
  }),
  moduleDef({
    moduleKey: 'franchises',
    label: 'Franchises',
    route: '/platform/franchises',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('statusChange', 'Status Change'),
    ],
  }),
  groupDef('website', 'Website Content'),
  moduleDef({
    moduleKey: 'website.home',
    label: 'Home Page',
    route: '/platform/marketing/home',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.successStories',
    label: 'Success Stories',
    route: '/platform/success-stories',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.testimonials',
    label: 'Testimonials',
    route: '/platform/testimonials',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.countries',
    label: 'Countries',
    route: '/platform/countries',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('duplicate', 'Duplicate'),
      mutationAction('publish', 'Publish'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.universities',
    label: 'Universities',
    route: '/platform/universities',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('publish', 'Publish'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.blogs',
    label: 'Blogs',
    route: '/platform/blogs',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('duplicate', 'Duplicate'),
      mutationAction('publish', 'Publish'),
      mutationAction('schedule', 'Schedule'),
      mutationAction('restoreVersion', 'Restore Version'),
    ],
  }),
  moduleDef({
    moduleKey: 'website.media',
    label: 'Media',
    route: '/platform/media',
    parentKey: 'website',
    actions: [
      readAction('read'),
      mutationAction('upload', 'Upload'),
      mutationAction('delete', 'Delete'),
    ],
  }),
  moduleDef({
    moduleKey: 'leads',
    label: 'Leads',
    route: '/platform/leads',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('assign', 'Assign'),
      mutationAction('import', 'Import'),
      mutationAction('export', 'Export'),
      mutationAction('merge', 'Merge'),
      mutationAction('removeDuplicates', 'Remove Duplicates'),
      mutationAction('archive', 'Archive'),
      mutationAction('statusChange', 'Status Change'),
      mutationAction('subStatusChange', 'Sub-status Change'),
      mutationAction('notesManage', 'Manage Notes'),
      mutationAction('tasksManage', 'Manage Tasks'),
      mutationAction('sendEmail', 'Send Email'),
      mutationAction('sendWhatsapp', 'Send WhatsApp'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'applicants',
    label: 'Enrolled Applicants',
    route: '/platform/applicants',
    actions: [
      readAction('read'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('assign', 'Assign'),
      mutationAction('import', 'Import'),
      mutationAction('export', 'Export'),
      mutationAction('archive', 'Archive'),
      mutationAction('statusChange', 'Status Change'),
      mutationAction('subStatusChange', 'Sub-status Change'),
      mutationAction('financeEdit', 'Edit Finance'),
      mutationAction('documentsManage', 'Manage Documents'),
      mutationAction('notesManage', 'Manage Notes'),
      mutationAction('tasksManage', 'Manage Tasks'),
      mutationAction('sendEmail', 'Send Email'),
      mutationAction('sendWhatsapp', 'Send WhatsApp'),
      mutationAction('upload', 'Upload'),
    ],
  }),
  moduleDef({
    moduleKey: 'featureRequests',
    label: 'Feature Requests',
    route: '/platform/feature-requests',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('comment', 'Comment'),
      mutationAction('statusChange', 'Status Change'),
      mutationAction('assign', 'Assign'),
      mutationAction('delete', 'Delete'),
    ],
  }),
  groupDef('crm', 'CRM'),
  moduleDef({
    moduleKey: 'crm.leadStatuses',
    label: 'Lead Statuses',
    route: '/platform/crm/lead-statuses',
    parentKey: 'crm',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('reorder', 'Reorder'),
      mutationAction('manageTransitions', 'Manage Transitions'),
    ],
  }),
  moduleDef({
    moduleKey: 'crm.applicantStatuses',
    label: 'Applicant Statuses',
    route: '/platform/crm/applicant-statuses',
    parentKey: 'crm',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('reorder', 'Reorder'),
      mutationAction('manageTransitions', 'Manage Transitions'),
    ],
  }),
  groupDef('staff', 'Staff Management'),
  moduleDef({
    moduleKey: 'staff.platformUsers',
    label: 'Platform Users',
    route: '/platform/users',
    parentKey: 'staff',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
    ],
  }),
  moduleDef({
    moduleKey: 'staff.rolePermissions',
    label: 'Roles & Permissions',
    route: '/platform/roles-permissions',
    parentKey: 'staff',
    actions: [
      readAction('read'),
      mutationAction('edit', 'Edit / Update'),
    ],
  }),
  groupDef('settings', 'Settings'),
  moduleDef({
    moduleKey: 'settings.general',
    label: 'General',
    route: '/platform/settings',
    parentKey: 'settings',
    actions: [
      readAction('read'),
      mutationAction('manageCron', 'Manage Cron'),
    ],
  }),
  moduleDef({
    moduleKey: 'settings.releaseNotes',
    label: 'Release Notes',
    route: '/platform/settings/release-notes',
    parentKey: 'settings',
    actions: [
      readAction('read'),
      mutationAction('create', 'Create'),
      mutationAction('edit', 'Edit / Update'),
      mutationAction('delete', 'Delete'),
      mutationAction('duplicate', 'Duplicate'),
      mutationAction('publish', 'Publish'),
    ],
  }),
];

const moduleByKey = new Map(
  PLATFORM_PERMISSION_REGISTRY.map((m) => [m.moduleKey, m] as const),
);

export function listPermissionModules(): PermissionModuleDef[] {
  return PLATFORM_PERMISSION_REGISTRY;
}

export function listLeafModules(): PermissionModuleDef[] {
  return PLATFORM_PERMISSION_REGISTRY.filter((m) => m.kind === 'module');
}

export function getPermissionModule(moduleKey: string): PermissionModuleDef | undefined {
  return moduleByKey.get(moduleKey);
}

export function isValidModuleKey(moduleKey: string): boolean {
  const mod = moduleByKey.get(moduleKey);
  return !!mod && mod.kind === 'module';
}

export function getActionDef(
  moduleKey: string,
  actionKey: string,
): PermissionActionDef | undefined {
  return getPermissionModule(moduleKey)?.actions.find((a) => a.key === actionKey);
}

export function isValidActionLevel(
  moduleKey: string,
  actionKey: string,
  level: string,
): level is PermissionLevel {
  if (!PERMISSION_LEVELS.includes(level as PermissionLevel)) return false;
  const action = getActionDef(moduleKey, actionKey);
  return !!action && action.allowedLevels.includes(level as PermissionLevel);
}

export function getAllowedLevels(moduleKey: string, actionKey: string): PermissionLevel[] {
  return getActionDef(moduleKey, actionKey)?.allowedLevels ?? [];
}

export function highestAllowedLevel(action: PermissionActionDef): PermissionLevel {
  if (action.allowedLevels.includes('EXECUTE')) return 'EXECUTE';
  if (action.allowedLevels.includes('READ_ONLY')) return 'READ_ONLY';
  return 'NO_ACCESS';
}

/** Resolve moduleKey for a platform URL path (longest route prefix wins). */
export function resolveModuleKeyForPath(pathname: string): string | undefined {
  const path = pathname.split('?')[0];
  let best: PermissionModuleDef | undefined;
  for (const mod of listLeafModules()) {
    if (!mod.route) continue;
    if (path === mod.route || path.startsWith(mod.route + '/')) {
      if (!best || (mod.route.length > (best.route?.length ?? 0))) {
        best = mod;
      }
    }
  }
  return best?.moduleKey;
}
